import { JSX, useState, useEffect } from 'react';

import { ChevronLeft, ChevronRight, CloudUpload, Error } from '@mui/icons-material';
import Home from '@mui/icons-material/Home';
import {
  Drawer,
  List,
  ListItemText,
  Divider,
  IconButton,
  Typography,
  ListItemButton,
  ListItemIcon,
  Box,
  useTheme,
  Theme,
  SxProps
} from '@mui/material';
import { Link } from 'react-router-dom';

import { SYNC_ROUTES, GENERAL_ROUTES, ERROR_ROUTES } from '../../config/routes';

interface MenuItem {
  id: string;
  text: string;
  icon?: JSX.Element;
  path: string;
}

interface SidebarProps {
  isAdmin: boolean;
  pageId?: string;
  onResize?: (width: number) => void;
}

// Extracted styles
const getStyles = (theme: Theme, isCollapsed: boolean) => ({
  drawer: {
    width: isCollapsed ? 60 : 250,
    flexShrink: 0,
    position: 'fixed',
    height: '100%',
    zIndex: theme.zIndex.drawer,
    '& .MuiDrawer-paper': {
      width: isCollapsed ? 60 : 250,
      position: 'static',
      boxSizing: 'border-box',
      transition: 'width 0.3s ease',
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText,
      borderRight: `1px solid ${theme.palette.primary.main}`,
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
    },
  } as SxProps,

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    p: 1,
    color: theme.palette.primary.contrastText,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    boxShadow: theme.shadows[3], // Using MUI's predefined shadow levels
  } as SxProps,

  headerTitle: {
    ml: 1,
    fontWeight: 'bold'
  } as SxProps,

  collapseButton: {
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  } as SxProps,

  sidebarContent: {
    overflowY: 'auto',
    flexGrow: 1,
  } as SxProps,

  sectionHeader: {
    px: 2,
    py: 1.5,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.primary.dark,
  } as SxProps,

  sectionIcon: {
    color: theme.palette.primary.main,
    mr: isCollapsed ? 0 : 1,
  } as SxProps,

  sectionTitle: {
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  } as SxProps,

  listItem: {
    minHeight: 48,
    px: 2.5,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  } as SxProps,

  activeListItem: {
    minHeight: 48,
    px: 2.5,
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  } as SxProps,

  listItemIcon: {
    minWidth: isCollapsed ? 0 : 36,
    color: theme.palette.primary.main
  } as SxProps,

  listItemText: {
    display: isCollapsed ? 'none' : 'block',
    '& .MuiTypography-root': {
      fontWeight: 500,
    }
  } as SxProps
});

export enum PageId {
  BuyerVendor = 'buyer_vendor',
  BuyerByProduct = 'buyer_by_product',
  DateMapping = 'date_mapping',
  Transactions = 'transactions',
  CustomerGroup = 'customer_group',
  ProductGroup = 'product_group',
  SalesRepDepartment = 'sales_rep_department',
  CreditReport = 'credit_report',
  Salesmen = 'salesmen',
  FixData = 'fix_data',
}

export const Sidebar = ({ isAdmin, pageId, onResize }: SidebarProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const theme = useTheme();
  const styles = getStyles(theme, isCollapsed);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    if (onResize) {
      onResize(isCollapsed ? 60 : 250);
    }
  }, [isCollapsed, onResize]);

  const generalMenuItems: MenuItem[] = GENERAL_ROUTES.map((route) => ({
    id: route.id,
    text: route.label,
    icon: route.icon,
    path: route.path
  }));

  const uploadMenuItems: MenuItem[] = SYNC_ROUTES.map((route) => ({
    id: route.id,
    text: route.label,
    icon: route.icon,
    path: route.path
  }));

  const errorMenuItems: MenuItem[] = ERROR_ROUTES.map((route) => ({
    id: route.id,
    text: route.label,
    icon: route.icon,
    path: route.path
  }));

  const renderMenuItems = (items: MenuItem[]) => {
    return items.map((item) => (
      <ListItemButton
        component={Link}
        to={item.path}
        key={item.id}
        sx={pageId === item.id ? styles.activeListItem : styles.listItem}
      >
        <ListItemIcon sx={styles.listItemIcon}>
          {item.icon}
        </ListItemIcon>
        <ListItemText primary={item.text} sx={styles.listItemText} />
      </ListItemButton>
    ));
  };

  return (
    <Box component="nav" sx={styles.drawer}>
      <Drawer
        variant="permanent"
        open={!isCollapsed}
        sx={styles.drawer}
      >
        {/* Fixed Header */}
        <Box sx={styles.header}>
          {!isCollapsed && (
            <Typography variant="h6" sx={styles.headerTitle}>
              Dashboard
            </Typography>
          )}
          <IconButton onClick={toggleCollapse} sx={styles.collapseButton}>
            {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Box>

        {/* Scrollable Content */}
        <Box sx={styles.sidebarContent}>
          <Divider />

          {/* General Section */}
          <Box sx={styles.sectionHeader}>
            <Home sx={styles.sectionIcon} />
            {!isCollapsed && (
              <Typography variant="subtitle1" sx={styles.sectionTitle}>
                General
              </Typography>
            )}
          </Box>
          <List>
            {renderMenuItems(generalMenuItems)}
          </List>
          <Divider />

          {/* Admin Errors Section */}
          {isAdmin && (
            <>
              <Box sx={styles.sectionHeader}>
                <Error sx={styles.sectionIcon} />
                {!isCollapsed && (
                  <Typography variant="subtitle1" sx={styles.sectionTitle}>
                    Errors
                  </Typography>
                )}
              </Box>
              <List>
                {renderMenuItems(errorMenuItems)}
              </List>
              <Divider />
            </>
          )}

          {/* Admin Uploads Section */}
          {isAdmin && (
            <>
              <Box sx={styles.sectionHeader}>
                <CloudUpload sx={styles.sectionIcon} />
                {!isCollapsed && (
                  <Typography variant="subtitle1" sx={styles.sectionTitle}>
                    Uploads
                  </Typography>
                )}
              </Box>
              <List>
                {renderMenuItems(uploadMenuItems)}
              </List>
              <Divider />
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};
